import { useEffect, useRef, useState } from 'react';
import { pluralize } from '@gonfalon/format/pluralize';
import { RequiredLabel } from '@gonfalon/launchpad-experimental/RequiredLabel';
import { Disclosure, DisclosurePanel, IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { AnimatePresence, m } from 'framer-motion';

import { trackFlagTargetingEvent } from '#utils/flags/flagAnalytics';
import { trackCreateApprovalRequestButtonClick } from '#utils/tracking/approvalRequest';

import type { SelectReviewerTagProps } from '../FormSection/Approvals/SelectReviewerTag';
import { SelectReviewerTag } from '../FormSection/Approvals/SelectReviewerTag';

import type { PendingChangesFormType } from './usePendingChangesFormAdapter';

import styles from './PendingChangesModal.module.css';

export interface SelectReviewersProps extends Omit<
  SelectReviewerTagProps,
  'onChange' | 'onBlur' | 'onClick' | 'description' | 'className' | 'placement'
> {
  form: PendingChangesFormType;
  approvalServiceKind?: string;
  minNumberOfApprovals: number;
  canBypassRequiredApprovals: boolean;
  bypassRequiredApprovals?: boolean;
}

export function SelectReviewersField({
  form,
  isRequired,
  minNumberOfApprovals,
  approvalServiceKind,
  canBypassRequiredApprovals,
  bypassRequiredApprovals,
  ...props
}: SelectReviewersProps) {
  const added = useRef(false);
  const description = isRequired
    ? `Select ${minNumberOfApprovals} ${pluralize(minNumberOfApprovals, 'reviewer', 'reviewers')} to approve your changes`
    : 'Add an optional reviewer to approve your changes.';

  return (
    <form.AppField name="reviewers">
      {(field) => (
        <SelectReviewerTag
          className={styles.formField}
          isRequired={isRequired}
          description={description}
          onChange={(reviewers) => {
            field.handleChange(reviewers);
            if (!added.current) {
              added.current = true;
              trackFlagTargetingEvent(isRequired ? 'Reviewer Added' : 'Optional Reviewer Added', {
                bypassRequiredApprovals,
                minNumberOfApprovals,
                approvalServiceKind,
                canBypassRequiredApprovals,
                isRequired,
              });
            }
          }}
          onBlur={field.handleBlur}
          onClick={() => {
            trackCreateApprovalRequestButtonClick({
              approvalSystem: approvalServiceKind,
              resourceKind: 'FlagConfiguration',
            });
          }}
          {...props}
        />
      )}
    </form.AppField>
  );
}

export interface SelectReviewersDisclosureProps extends SelectReviewersProps {
  canBypassRequiredApprovals: boolean;
  showOptionalReviewers: boolean;
  showReviewerInline: boolean;
  showReviewerInFooter: boolean;
  setShowOptionalReviewers: (showOptionalReviewers: boolean) => void;
}

export function SelectReviewersDisclosure({
  canBypassRequiredApprovals,
  showOptionalReviewers,
  showReviewerInline,
  showReviewerInFooter,
  setShowOptionalReviewers,
  ...props
}: SelectReviewersDisclosureProps) {
  const { form } = props;
  const bypassRequiredApprovals = form.state.values.bypassRequiredApprovals;
  const isApprovalRequired = !bypassRequiredApprovals && props.isRequired;
  const [isExpanded, setIsExpanded] = useState(!!(isApprovalRequired && canBypassRequiredApprovals));
  const showInlineReviewersField =
    // Approval is required and bypass is not allowed
    (isApprovalRequired && !canBypassRequiredApprovals) ||
    // User has opted to select optional reviewers
    showOptionalReviewers ||
    // when `enable-alternate-reviewer-field` === ''
    !(showReviewerInline || showReviewerInFooter);

  // When bypassRequiredApprovals is toggled from the save button, update the expanded state
  useEffect(() => {
    if (bypassRequiredApprovals) {
      setIsExpanded(false);
    }
  }, [bypassRequiredApprovals]);

  if (showInlineReviewersField) {
    return (
      <SelectReviewersField
        label="Reviewer(s)"
        bypassRequiredApprovals={bypassRequiredApprovals}
        canBypassRequiredApprovals={canBypassRequiredApprovals}
        {...props}
      />
    );
  }

  return (
    <Disclosure className={styles.disclosureMenu} isExpanded={isExpanded}>
      <span slot="trigger" className={styles.disclosureTrigger}>
        <RequiredLabel htmlFor="notifyMembers" isRequired={isApprovalRequired}>
          Request approval
        </RequiredLabel>
        <TooltipTrigger>
          <IconButton
            aria-label={isExpanded ? 'Bypass approvals' : 'Request reviewers'}
            data-test-id="select-reviewers"
            icon={isExpanded ? 'minus' : 'add'}
            className={styles.disclosureTriggerIcon}
            variant="minimal"
            size="small"
            onPress={() => {
              if (isExpanded) {
                form.setFieldValue('reviewers', []);
              }
              if (canBypassRequiredApprovals) {
                form.setFieldValue('bypassRequiredApprovals', isExpanded);

                if (!isExpanded) {
                  trackFlagTargetingEvent('Bypass Approvals Clicked');
                }
              }

              if (showReviewerInFooter) {
                setShowOptionalReviewers(!isExpanded);
              }

              setIsExpanded(!isExpanded);
              void form.validate('change');
            }}
          />

          <Tooltip>{isExpanded ? 'Save without approval' : 'Request approval'}</Tooltip>
        </TooltipTrigger>
      </span>

      <AnimatePresence>
        <DisclosurePanel className={styles.panel}>
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto', paddingBottom: 'var(--lp-spacing-400)' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            }}
            className={styles.disclosureContent}
          >
            <SelectReviewersField
              label=""
              bypassRequiredApprovals={bypassRequiredApprovals}
              canBypassRequiredApprovals={canBypassRequiredApprovals}
              {...props}
            />
          </m.div>
        </DisclosurePanel>
      </AnimatePresence>
    </Disclosure>
  );
}
